import React, { useEffect, useState } from "react";
import { takePendingQuoteService, QUOTE_SERVICE_EVENT } from "../../lib/quoteIntent";
import { CircleCheckBig } from "lucide-react";
import { Toaster } from "../ui/toaster";
import { useQuoteCalculator } from "../../hooks/useQuoteCalculator";
import { useQuoteForm } from "../../hooks/useQuoteForm";
import { usePricing } from "../../hooks/usePricing";
import { LAUNDRY_SERVICE, CARPET_SERVICE } from "../../lib/pricing";
import QuoteCalculator from "./QuoteCalculator";
import QuoteForm from "./QuoteForm";
import QuoteFormAIAssistant from "./QuoteFormAIAssistant";

/* ── Quote Section Header ── */

const QuoteSectionHeader = () => (
  <div className="text-center mb-12">
    <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
      Get Your Free Cleaning Quote
    </h3>
    <p className="text-lg text-slate-600">
      Use our calculator for an instant estimate, then tell us about your property
    </p>
    <div className="mt-4 inline-flex items-center gap-2 text-green-600 font-semibold">
      <CircleCheckBig className="w-5 h-5" />
      Response within 15 minutes during business hours
    </div>
  </div>
);

/* ── Main Quote Section ── */

const QuoteSection = () => {
  const [selectedService, setSelectedService] = useState(() => takePendingQuoteService() || "Regular Cleaning");
  useEffect(() => {
    const onPick = (e) => { takePendingQuoteService(); setSelectedService(e.detail); };
    window.addEventListener(QUOTE_SERVICE_EVENT, onPick);
    return () => window.removeEventListener(QUOTE_SERVICE_EVENT, onPick);
  }, []);
  const [bedrooms, setBedrooms] = useState(2);
  const [bathrooms, setBathrooms] = useState(1);
  const [toilets, setToilets] = useState(0);
  const [frequency, setFrequency] = useState("Weekly");
  const [suppliesProvided, setSuppliesProvided] = useState(false);
  const [linensIncluded, setLinensIncluded] = useState(false);
  const [parkingAvailable, setParkingAvailable] = useState(false);
  const [laundry, setLaundry] = useState({ kg: 5, ironing: 0, duvets: 0, suits: 0 });
  const [carpet, setCarpet] = useState({ rooms: 1, livingRooms: 0, stairsHallways: 0, stainProtection: false });
  
  // Oven Cleaning specific fields
  const [ovenOptions, setOvenOptions] = useState({
    singleOven: false,
    doubleOven: false,
    agaRange: false,
    hob: false,
    extractorFan: false,
  });
  
  // Commercial Cleaning specific fields
  const [squareFootage, setSquareFootage] = useState(1000);
  const [numberOfOffices, setNumberOfOffices] = useState(1);

  const pricing = usePricing();
  const { estimatedPrice, estimatedDuration, breakdown } = useQuoteCalculator(
    selectedService,
    bedrooms,
    frequency,
    ovenOptions,
    squareFootage,
    bathrooms,
    toilets,
    pricing,
    laundry,
    carpet
  );

  const { formData, photos, setPhotos, submitted, resetSubmitted, submitting, handleFormChange, handleSubmit } = useQuoteForm();

  const onSubmit = (e) => {
    const baseData = {
      selectedService,
      frequency,
      estimatedPrice,
      estimatedDuration,
      suppliesProvided,
      parkingAvailable,
    };

    // Add service-specific fields
    if (selectedService === CARPET_SERVICE) {
      handleSubmit(e, {
        ...baseData,
        carpet,
        bedrooms: Math.max(1, carpet.rooms),
        bathrooms: 0,
        toilets: 0,
        suppliesProvided: false,
        linensIncluded: false,
      });
    } else if (selectedService === LAUNDRY_SERVICE) {
      handleSubmit(e, {
        ...baseData,
        laundry,
        bedrooms: 1,
        bathrooms: 0,
        toilets: 0,
        suppliesProvided: false,
        linensIncluded: false,
      });
    } else if (selectedService === "Oven Cleaning") {
      handleSubmit(e, {
        ...baseData,
        ovenOptions,
        bedrooms: 1, // Default value for backend compatibility
        bathrooms: 0,
        toilets: 0,
        linensIncluded: false,
      });
    } else if (selectedService === "Commercial Cleaning") {
      handleSubmit(e, {
        ...baseData,
        squareFootage,
        numberOfOffices,
        bathrooms,
        toilets,
        bedrooms: 1, // Not relevant for commercial
        linensIncluded: false,
      });
    } else {
      handleSubmit(e, {
        ...baseData,
        bedrooms,
        bathrooms,
        toilets,
        linensIncluded: selectedService === "Airbnb Cleaning" ? linensIncluded : false,
      });
    }
  };

  const handleServiceRecommendation = (serviceName) => {
    // Update the selected service when AI recommends one
    setSelectedService(serviceName);
  };

  return (
    <section id="quote-section" className="scroll-mt-32 py-20 bg-white">
      <Toaster />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <QuoteSectionHeader />

        {/* AI Assistant - positioned above the calculator and form */}
        <div className="mb-8 flex justify-center">
          <QuoteFormAIAssistant onServiceRecommendation={handleServiceRecommendation} />
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <QuoteCalculator
            selectedService={selectedService}
            setSelectedService={setSelectedService}
            bedrooms={bedrooms}
            setBedrooms={setBedrooms}
            bathrooms={bathrooms}
            setBathrooms={setBathrooms}
            toilets={toilets}
            setToilets={setToilets}
            frequency={frequency}
            setFrequency={setFrequency}
            suppliesProvided={suppliesProvided}
            setSuppliesProvided={setSuppliesProvided}
            linensIncluded={linensIncluded}
            setLinensIncluded={setLinensIncluded}
            parkingAvailable={parkingAvailable}
            setParkingAvailable={setParkingAvailable}
            ovenOptions={ovenOptions}
            setOvenOptions={setOvenOptions}
            squareFootage={squareFootage}
            setSquareFootage={setSquareFootage}
            numberOfOffices={numberOfOffices}
            setNumberOfOffices={setNumberOfOffices}
            estimatedPrice={estimatedPrice}
            estimatedDuration={estimatedDuration}
            pricing={pricing}
            breakdown={breakdown}
            laundry={laundry}
            setLaundry={setLaundry}
            carpet={carpet}
            setCarpet={setCarpet}
          />
          <QuoteForm
            formData={formData}
            onFormChange={handleFormChange}
            onSubmit={onSubmit}
            submitted={submitted}
            submitting={submitting}
            photos={photos}
            setPhotos={setPhotos}
            onReset={resetSubmitted}
          />
        </div>
      </div>
    </section>
  );
};

export default QuoteSection;
